import KmxHeartFilled from '@kmx/mui-icons/KmxHeartFilled';
import type { IconButtonProps } from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import { memo, type MouseEventHandler, useCallback } from 'react';

import type { FavoritesConfig } from '@/types/FavoritesConfig';

import ActionIconButton from '../ActionIconButton/ActionIconButton';

const StyledButton = styled(ActionIconButton)(({ theme }) => ({
  '.MuiSvgIcon-root': {
    stroke: 'currentColor',
    strokeWidth: '1.25px',

    path: {
      fill: 'transparent',

      '.kmx-car-tile[data-size="small"] &': {
        fill: theme.palette.background.paper,
      },
    },
  },

  '&[data-favorited="true"]': {
    // Note: Duplicate class increases specificity.
    '.MuiSvgIcon-root.MuiSvgIcon-root': {
      stroke: theme.palette.flagged.light,

      path: {
        fill: theme.palette.flagged.light,
      },
    },
  },
}));

type FavoriteButtonProps = Omit<IconButtonProps, 'onClick'> & {
  isFavorited?: FavoritesConfig['isFavorited'];
  onClick: FavoritesConfig['onChange'];
  stockNumber: string;
};

function FavoriteButton({
  isFavorited = false,
  stockNumber,
  onClick,
  ...iconButtonProps
}: FavoriteButtonProps) {
  const handleFavoriteClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      event.preventDefault();
      onClick(!isFavorited);
    },
    [isFavorited, onClick],
  );

  const clickProps = `Element type: Car Tile Save Car,StockNumber: ${stockNumber},Is selected: ${isFavorited}`;

  return (
    <StyledButton
      {...iconButtonProps}
      aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
      className="kmx-car-tile__favorite-button"
      data-clickprops={clickProps}
      data-favorited={isFavorited ?? false}
      data-ignore-anchor-tag-rule
      onClick={handleFavoriteClick}
      title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
    >
      <KmxHeartFilled fontSize="inherit" />
    </StyledButton>
  );
}

export default memo(FavoriteButton);
